import { format } from "date-fns";
import { type ComponentProps, type FC, useMemo } from "react";

import { DatePicker, Icon, parseDatePickerValue } from "../../components";
import { cn } from "../../utils";
import { FIELD_BORDERLESS_CLASS_NAME } from "./field.constants";
import { FieldBase, type FieldBaseProps } from "./field-base";
import { useField } from "./use-field";

export type FieldDatePickerProps = FieldBaseProps & {
  placeholder?: string;
  borderless?: boolean;
  dateFormat?: string;
  fromYear?: number;
  toYear?: number;
  hideCalendarIcon?: boolean;
  onChange?: ComponentProps<typeof DatePicker>["onChange"];
  onBlur?: ComponentProps<typeof DatePicker>["onBlur"];
  inputProps?: ComponentProps<typeof DatePicker>;
};

export const FieldDatePicker: FC<FieldDatePickerProps> = ({
  inputProps,
  borderless,
  placeholder = "Pick a date",
  dateFormat = "MMMM d, yyyy",
  fromYear,
  toYear,
  onChange,
  onBlur,
  hideCalendarIcon,
  readOnly,
  ...props
}) => {
  const { field, hasError } = useField({ name: props.name, disabled: props.disabled, inputProps });

  const handleChange: FieldDatePickerProps["onChange"] = (event) => {
    field.onChange(event || "");
    onChange?.(event);
  };

  const handleBlur: FieldDatePickerProps["onBlur"] = (event) => {
    field.onBlur();
    onBlur?.(event);
  };

  const fieldValueAsDate = useMemo(() => parseDatePickerValue(field.value), [field.value]);

  return (
    <FieldBase {...props}>
      <DatePicker
        {...field}
        {...inputProps}
        calendarProps={{
          fromYear,
          toYear,
          ...inputProps?.calendarProps,
        }}
        onChange={handleChange}
        onBlur={handleBlur}
        variant="outline"
        className={cn(
          "min-w-[180px] justify-start text-left font-normal px-3 text-sm bg-background",
          !field.value && "text-muted-foreground",
          {
            "border-destructive": hasError && !field.disabled,
            [FIELD_BORDERLESS_CLASS_NAME]: borderless,
          },
          inputProps?.className
        )}
        readOnly={readOnly}
      >
        {!hideCalendarIcon && <Icon icon="calendar_today" className="size-4 text-base text-foreground opacity-50" />}
        {fieldValueAsDate ? format(fieldValueAsDate, dateFormat) : <span>{placeholder}</span>}
      </DatePicker>
    </FieldBase>
  );
};
